// seo.ts
import { SHOP_PUBLIC_ENABLED } from './utils/shopPublic';
export interface PageSpecificData {
  page?: number | string;
  categoryName?: string;
  articleTitle?: string;
  articleSummary?: string;
  articleImage?: string;
  productName?: string;
  productDescription?: string;
  productImage?: string;
  title?: string;
  description?: string;
  canonicalUrl?: string;
  robots?: 'index,follow' | 'noindex,follow';
  schema?: object | object[];
}

export interface SeoOutput {
  title: string;
  description: string;
  ogType: 'website' | 'article' | 'product';
  ogImage: string;
  canonicalUrl: string;
  robots: 'index,follow' | 'noindex,follow';
  schema: object | object[] | null;
}

const BASE_URL = 'https://esoteric.pl';
const BASE_TITLE = 'esoteric.pl';
const DEFAULT_OG_IMAGE = 'https://assets.esoteric.pl/img/esoteric-banner.jpg';

const sanitizeDescription = (text?: string, maxLength: number = 155) => {
    if (!text) return '';
    // Strip HTML tags and normalize whitespace
    const stripped = text.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim();
    if (stripped.length <= maxLength) return stripped;
    // Trim to the nearest word before maxLength
    return stripped.substring(0, stripped.lastIndexOf(' ', maxLength)) + '...';
};

export const getSeoData = (pathname: string, data: PageSpecificData = {}): SeoOutput => {
  const finalPath = data.canonicalUrl || pathname;
  const canonicalUrl = finalPath.startsWith('http') ? finalPath : `${BASE_URL}${finalPath}`;
  const robots = data.robots || 'index,follow';
  const schema = data.schema || null;
  const pageSuffix = data.page && Number(data.page) > 1 ? ` - Strona ${data.page}` : '';

  let title = `${BASE_TITLE} - Portal Ezoteryczny Numer #1`;
  let description = SHOP_PUBLIC_ENABLED
    ? 'Odkryj świat ezoteryki. Profil wróżek, sklep z magicznymi przedmiotami, blog z wiedzą ezoteryczną. Twoje centrum duchowego rozwoju.'
    : 'Odkryj świat ezoteryki. Profil wróżek, horoskopy, fazy księżyca i blog z wiedzą ezoteryczną. Twoje centrum duchowego rozwoju.';
  let ogType: SeoOutput['ogType'] = 'website';
  let ogImage = DEFAULT_OG_IMAGE;

  // Specific Routes
  if (pathname === '/') {
    // default values are fine
  } else if (pathname.startsWith('/blog') && !data.articleTitle && !data.categoryName) {
    title = `Blog - Wiedza Ezoteryczna${pageSuffix} - ${BASE_TITLE}`;
    description = 'Miejsce spotkań z astrologią, kartami i praktykami, które pomagają lepiej zrozumieć siebie i świat wokół.';
  } else if (pathname === '/sklep' && !data.productName) {
    title = `Sklep Ezoteryczny - Kamienie, Świece, Rytuały - ${BASE_TITLE}`;
    description = 'Znajdź amulety, karty, kamienie, świece i inne artefakty wybrane specjalnie dla Ciebie. Ręcznie pakowane z intencją.';
  } else if (pathname === '/koszyk') {
    title = `Koszyk - ${BASE_TITLE}`;
    description = 'Przejrzyj swoje magiczne produkty i przygotuj się do finalizacji zamówienia.';
  } else if (pathname === '/zamowienie') {
    title = `Zamówienie - ${BASE_TITLE}`;
    description = 'Podaj dane do dostawy i sfinalizuj swoje zamówienie.';
  } else if (pathname === '/zamowienie-zlozone') {
    title = `Zamówienie Złożone - Dziękujemy! - ${BASE_TITLE}`;
    description = 'Twoje zamówienie zostało pomyślnie złożone. Wysłaliśmy potwierdzenie na Twój adres email.';
  } else if (pathname === '/czat') {
    title = `Czat - ${BASE_TITLE}`;
    description = 'Porozmawiaj na żywo z naszymi ekspertami. Uzyskaj porady i odpowiedzi na swoje pytania dotyczące tarota, astrologii i duchowości.';
  } else if (pathname === '/404') {
    title = `404 Nie znaleziono strony - ${BASE_TITLE}`;
    description = 'Strona, której szukasz, zniknęła w eterze. Wróć na stronę główną, by kontynuować swoją podróż.';
  } else if (pathname.startsWith('/cms')) {
    title = `CMS - ${BASE_TITLE}`;
    description = 'Panel zarządzania treścią.';
  } else if (pathname === '/kontakt') {
    title = `Kontakt - ${BASE_TITLE}`;
    description = 'Skontaktuj się z nami w sprawie pytań, współpracy lub wsparcia. Jesteśmy tu, aby pomóc.';
  } else if (pathname === '/sekcja-prawna') {
    title = `Informacje Prawne - Regulamin i Polityka Prywatności - ${BASE_TITLE}`;
    description = 'Zapoznaj się z regulaminem, polityką prywatności, informacjami o zwrotach i wysyłkach.';
  } else if (pathname === '/faq') {
    title = `Częste Pytania (FAQ) - ${BASE_TITLE}`;
    description = 'Znajdź odpowiedzi na najczęściej zadawane pytania dotyczące zamówień, dostawy, produktów i zwrotów.';
  } else if (pathname === '/dla-influencerow') {
    title = `Współpraca dla Influencerów - ${BASE_TITLE}`;
    description = 'Jesteś twórcą internetowym? Dołącz do naszego magicznego kręgu i twórzmy razem niezwykłe treści.';
  } else if (pathname === '/wspolpraca') {
    title = `Współpraca Biznesowa - ${BASE_TITLE}`;
    description = 'Jesteś dostawcą, rzemieślnikiem lub partnerem biznesowym? Zapraszamy do rozmowy o współpracy.';
  }
  
  // Dynamic Content
  if (data.articleTitle) {
    title = `${data.articleTitle} - ${BASE_TITLE}`;
    description = sanitizeDescription(data.articleSummary);
    ogImage = data.articleImage || DEFAULT_OG_IMAGE;
    ogType = 'article';
  } else if (data.productName) {
    title = `${data.productName} - ${BASE_TITLE}`;
    description = sanitizeDescription(data.productDescription, 155) || `Odkryj magiczne właściwości produktu ${data.productName}. Dostępny w naszym sklepie ezoterycznym.`;
    ogImage = data.productImage || DEFAULT_OG_IMAGE;
    ogType = 'product';
  } else if (data.categoryName) {
    const name = data.categoryName.charAt(0).toUpperCase() + data.categoryName.slice(1);
    title = `${name}${pageSuffix} - ${BASE_TITLE}`;
    description = `Artykuły z kategorii ${name}. Odkryj wiedzę na temat ${name} i pogłębiaj swoją duchową ścieżkę.`;
    // ogImage for category could be set here if available in data
  }

  // FIX: Apply direct title and description overrides if they are passed in `data`.
  if (data.title) {
    title = data.title;
  }
  if (data.description) {
    description = data.description;
  }

  return { title, description, ogType, ogImage, canonicalUrl, robots, schema };
};